import type {
  AdminUser,
  AleResult,
  AuthStatus,
  CountingProgress,
  Phase,
  PollSettings,
  PollStatus,
  Timeline,
} from '@val/shared'
import { USE_MOCK_FALLBACK } from './config.ts'
import { mockProgress, mockResult, mockTimeline } from './mockResult.ts'

class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

/** true om felet betyder "ingen fungerande backend" (nätfel eller 5xx). */
function isBackendDown(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 0 || err.status >= 500)
}

async function getJson<T>(url: string): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, { headers: { accept: 'application/json' }, credentials: 'same-origin' })
  } catch {
    throw new ApiError(0, `${url}: ingen kontakt`)
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    throw new ApiError(res.status, body?.error ?? `${url} → ${res.status}`)
  }
  return (await res.json()) as T
}

async function sendJson<T>(url: string, method: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin' }
  // Sätt content-type BARA när det finns en kropp – annars avvisar Fastify (400).
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' }
    init.body = JSON.stringify(body)
  }
  let res: Response
  try {
    res = await fetch(url, init)
  } catch {
    throw new ApiError(0, `${url}: ingen kontakt`)
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new ApiError(res.status, (data as { error?: string }).error ?? `${url} → ${res.status}`)
  }
  return data as T
}

/** Utan `phase` använder servern den fas som är vald på admin-sidan. */
export async function fetchResult(phase?: Phase): Promise<AleResult> {
  try {
    return await getJson<AleResult>(`/api/result${phase ? `?phase=${phase}` : ''}`)
  } catch (err) {
    if (USE_MOCK_FALLBACK) return mockResult(phase ?? 'preliminar')
    throw err
  }
}

export async function fetchTimeline(phase?: Phase): Promise<Timeline> {
  try {
    return await getJson<Timeline>(`/api/timeline${phase ? `?phase=${phase}` : ''}`)
  } catch (err) {
    if (USE_MOCK_FALLBACK) return mockTimeline(phase ?? 'preliminar')
    throw err
  }
}

/**
 * Hur långt räkningen kommit nationellt (riksdagsvalet) – en snabbare
 * indikator medan kommunens egen mandatfördelning ännu inte är publicerad.
 */
export async function fetchProgress(phase?: Phase): Promise<CountingProgress> {
  try {
    return await getJson<CountingProgress>(`/api/progress${phase ? `?phase=${phase}` : ''}`)
  } catch (err) {
    if (USE_MOCK_FALLBACK) return mockProgress(phase ?? 'preliminar')
    throw err
  }
}

/* ---- mock-session (ingen backend, lokal utveckling) ---- */

const MOCK_SESSION_KEY = 'val.mockSession'
const MOCK_USERS_KEY = 'val.mockUsers'

const mockSession = {
  get: () => {
    try {
      return sessionStorage.getItem(MOCK_SESSION_KEY)
    } catch {
      return null
    }
  },
  set: (u: string | null) => {
    try {
      if (u) sessionStorage.setItem(MOCK_SESSION_KEY, u)
      else sessionStorage.removeItem(MOCK_SESSION_KEY)
    } catch {
      /* ignorera */
    }
  },
}

const mockUsers = {
  list: (): AdminUser[] => {
    try {
      return JSON.parse(sessionStorage.getItem(MOCK_USERS_KEY) ?? '[]') as AdminUser[]
    } catch {
      return []
    }
  },
  save: (u: AdminUser[]) => {
    try {
      sessionStorage.setItem(MOCK_USERS_KEY, JSON.stringify(u))
    } catch {
      /* ignorera */
    }
  },
}

function mockPassword() {
  return Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6)
}

/** I mock-läge är bara användarnamnet "admin" administratör (så rollstyrning går att testa). */
const mockIsAdmin = (username: string) => username === 'admin'

/* ---- auth ---- */

export async function fetchAuthStatus(): Promise<AuthStatus> {
  try {
    return await getJson<AuthStatus>('/api/me')
  } catch {
    if (USE_MOCK_FALLBACK) {
      const u = mockSession.get()
      return u
        ? { authenticated: true, username: u, isAdmin: mockIsAdmin(u) }
        : { authenticated: false }
    }
    return { authenticated: false }
  }
}

export async function login(username: string, password: string): Promise<AuthStatus> {
  let res: Response | null = null
  try {
    res = await fetch('/api/login', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ username, password }),
    })
  } catch {
    res = null
  }

  if (res?.status === 401) throw new ApiError(401, 'Fel användarnamn eller lösenord')
  if (res?.ok) return (await res.json()) as AuthStatus

  if (USE_MOCK_FALLBACK && username && password) {
    mockSession.set(username)
    return { authenticated: true, username, isAdmin: mockIsAdmin(username) }
  }
  throw new ApiError(res?.status ?? 0, 'Ingen kontakt med servern')
}

export async function logout(): Promise<void> {
  mockSession.set(null)
  await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {})
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  try {
    await sendJson('/api/me/password', 'POST', { currentPassword, newPassword })
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) return
    throw err
  }
}

/* ---- användarhantering ---- */

export async function fetchUsers(): Promise<AdminUser[]> {
  try {
    return (await getJson<{ users: AdminUser[] }>('/api/admin/users')).users
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) return mockUsers.list()
    throw err
  }
}

export async function createUser(
  username: string,
  isAdmin: boolean,
): Promise<{ username: string; password: string }> {
  try {
    return await sendJson('/api/admin/users', 'POST', { username, isAdmin })
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) {
      const list = mockUsers.list()
      if (list.some((u) => u.username === username))
        throw new ApiError(400, 'Användaren finns redan')
      list.push({
        username,
        isAdmin,
        createdAt: new Date().toISOString(),
        fromSeed: false,
        lastLoginAt: null,
      })
      mockUsers.save(list)
      return { username, password: mockPassword() }
    }
    throw err
  }
}

export async function resetUserPassword(
  username: string,
): Promise<{ username: string; password: string }> {
  try {
    return await sendJson(`/api/admin/users/${encodeURIComponent(username)}/reset`, 'POST')
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) {
      return { username, password: mockPassword() }
    }
    throw err
  }
}

export async function setUserAdmin(username: string, isAdmin: boolean): Promise<void> {
  try {
    await sendJson(`/api/admin/users/${encodeURIComponent(username)}/role`, 'POST', { isAdmin })
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) {
      const list = mockUsers.list().map((u) => (u.username === username ? { ...u, isAdmin } : u))
      mockUsers.save(list)
      return
    }
    throw err
  }
}

export async function deleteUser(username: string): Promise<void> {
  try {
    await sendJson(`/api/admin/users/${encodeURIComponent(username)}`, 'DELETE')
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) {
      mockUsers.save(mockUsers.list().filter((u) => u.username !== username))
      return
    }
    throw err
  }
}

/* ---- hämtningsinställningar ---- */

const MOCK_SETTINGS_KEY = 'val.mockSettings'
const mockStatus = (): PollStatus => {
  let s: PollSettings = { pollEnabled: false, pollIntervalMs: 60_000, activePhase: 'genrep' }
  try {
    const raw = sessionStorage.getItem(MOCK_SETTINGS_KEY)
    if (raw) s = { ...s, ...(JSON.parse(raw) as PollSettings) }
  } catch {
    /* ignorera */
  }
  return {
    ...s,
    lastFetchedAt: null,
    lastOutcome: 'demoläge – ingen backend',
    minIntervalMs: 15_000,
    maxIntervalMs: 24 * 60 * 60 * 1000,
  }
}

export async function fetchSettings(): Promise<PollStatus> {
  try {
    return await getJson<PollStatus>('/api/admin/settings')
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) return mockStatus()
    throw err
  }
}

export async function updateSettings(patch: Partial<PollSettings>): Promise<PollStatus> {
  try {
    return await sendJson<PollStatus>('/api/admin/settings', 'PUT', patch)
  } catch (err) {
    if (USE_MOCK_FALLBACK && isBackendDown(err)) {
      const next = { ...mockStatus(), ...patch }
      try {
        sessionStorage.setItem(
          MOCK_SETTINGS_KEY,
          JSON.stringify({
            pollEnabled: next.pollEnabled,
            pollIntervalMs: next.pollIntervalMs,
            activePhase: next.activePhase,
          }),
        )
      } catch {
        /* ignorera */
      }
      return next
    }
    throw err
  }
}

export async function refreshNow(): Promise<{ changed: boolean; reason: string }> {
  // force=1: räkna om från Valmyndighetens data på nytt även om filens md5 är
  // oförändrad. Rimligt förval för en knapp man klickar på just för att man
  // vill ha ett färskt svar – annars kan ett cachat resultat (t.ex. beräknat
  // med gammal kod) sitta kvar tills källfilen faktiskt ändras.
  return sendJson('/api/admin/refresh?force=1', 'POST')
}

export { ApiError }
